import { config } from '@/data/config';
import { CompilacoesHttpError } from '@/data/http';
import { getAuthPort } from '@/data/auth';
import type {
  CapabilitiesDoUsuario,
  CompilacoesDataSource,
  EventoCapability,
  UsuarioComCapabilities,
  UsuarioKeycloak,
} from '@/data/types';
import { trackRequest } from '@/lib/request-progress';
import { CAPABILITIES_GERENCIAVEIS } from '@/lib/admin-capabilities';

/**
 * Administração de capabilities em memória (modo demo).
 *
 * Mesmas regras da API: só `@nasajon.com.br` na busca, capability desconhecida
 * dá 422 e mexer em `sysadmin` sem ser sysadmin dá 403 (quem é sysadmin vem de
 * `VITE_DEMO_CAPABILITIES`, via a porta de autenticação). Erros no formato
 * `{"detail": ...}`. Tudo se perde ao recarregar a página.
 */

const ATOR_DEMO = 'demo@nasajon.com.br';

const USUARIOS: UsuarioKeycloak[] = [
  ['u-001', 'ana.souza', 'Ana Souza'],
  ['u-002', 'bruno.lima', 'Bruno Lima'],
  ['u-003', 'carla.mendes', 'Carla Mendes'],
  ['u-004', 'diego.rocha', 'Diego Rocha'],
  ['u-005', 'elisa.prado', 'Elisa Prado'],
  ['u-006', 'fabio.nunes', 'Fábio Nunes'],
  ['u-007', 'paulo.basilio', 'Paulo Basílio'],
].map(([id, username, nome]) => ({
  keycloak_user_id: id,
  username,
  nome,
  email: `${username}@nasajon.com.br`,
}));

interface EstadoAdmin {
  capabilities: Map<string, string[]>;
  eventos: EventoCapability[];
  proximoId: number;
}

let estado: EstadoAdmin | null = null;

function criarEstado(): EstadoAdmin {
  const agora = Date.now();
  const antes = (minutos: number) => new Date(agora - minutos * 60_000).toISOString();
  const evento = (
    id: number,
    alvo: string,
    capability: string,
    acao: 'conceder' | 'revogar',
    minutos: number,
  ): EventoCapability => ({
    id,
    ator: ATOR_DEMO,
    alvo_id: alvo,
    alvo_email: USUARIOS.find((item) => item.keycloak_user_id === alvo)?.email ?? null,
    capability,
    acao,
    criado_em: antes(minutos),
  });
  return {
    capabilities: new Map([
      ['u-001', ['admin', 'maintainer']],
      ['u-002', ['maintainer']],
      ['u-003', ['painel', 'sysadmin']],
    ]),
    eventos: [
      evento(5, 'u-002', 'painel', 'revogar', 35),
      evento(4, 'u-003', 'sysadmin', 'conceder', 60 * 5),
      evento(3, 'u-003', 'painel', 'conceder', 60 * 26),
      evento(2, 'u-002', 'maintainer', 'conceder', 60 * 24 * 4),
      evento(1, 'u-001', 'admin', 'conceder', 60 * 24 * 9),
    ],
    proximoId: 6,
  };
}

function obterEstado(): EstadoAdmin {
  estado ??= criarEstado();
  return estado;
}

/** Só para testes: volta ao estado inicial na próxima chamada. */
export function resetDemoAdmin(): void {
  estado = null;
}

function erro(recurso: string, status: number, detail: string): CompilacoesHttpError {
  return new CompilacoesHttpError(recurso, status, JSON.stringify({ detail }));
}

function esperar(signal?: AbortSignal): Promise<void> {
  const ms = config.demoLatenciaMs;
  return new Promise((resolver, rejeitar) => {
    if (signal?.aborted) return rejeitar(new DOMException('Abortado', 'AbortError'));
    if (ms <= 0) return resolver();
    const id = setTimeout(resolver, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(id);
      rejeitar(new DOMException('Abortado', 'AbortError'));
    });
  });
}

async function simular<T>(signal: AbortSignal | undefined, gerar: () => T): Promise<T> {
  return trackRequest(async () => {
    await esperar(signal);
    return structuredClone(gerar());
  });
}

function acharUsuario(recurso: string, id: string): UsuarioKeycloak {
  const usuario = USUARIOS.find((item) => item.keycloak_user_id === id);
  if (!usuario) throw erro(recurso, 404, 'Usuário não encontrado no Keycloak.');
  return usuario;
}

function validarEscrita(recurso: string, capability: string, roles: string[]): void {
  if (!CAPABILITIES_GERENCIAVEIS.some((item) => item.valor === capability)) {
    throw erro(recurso, 422, `Capability desconhecida: ${capability}.`);
  }
  if (capability === 'sysadmin' && !roles.includes('sysadmin')) {
    throw erro(recurso, 403, 'Só quem já é sysadmin pode conceder ou revogar a capability sysadmin.');
  }
}

function registrar(usuario: UsuarioKeycloak, capability: string, acao: 'conceder' | 'revogar'): void {
  const e = obterEstado();
  e.eventos.unshift({
    id: e.proximoId++,
    ator: ATOR_DEMO,
    alvo_id: usuario.keycloak_user_id,
    alvo_email: usuario.email,
    capability,
    acao,
    criado_em: new Date().toISOString(),
  });
}

type MetodosAdmin = Pick<
  CompilacoesDataSource,
  | 'listarUsuariosComCapabilities'
  | 'buscarUsuariosKeycloak'
  | 'obterCapabilitiesDoUsuario'
  | 'concederCapability'
  | 'revogarCapability'
  | 'listarEventosCapabilities'
>;

export const demoAdmin: MetodosAdmin = {
  listarUsuariosComCapabilities(signal) {
    return simular(signal, (): UsuarioComCapabilities[] =>
      [...obterEstado().capabilities.entries()]
        .filter(([, caps]) => caps.length > 0)
        .map(([id, capabilities]) => ({
          ...acharUsuario('os usuários', id),
          capabilities,
        }))
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    );
  },

  buscarUsuariosKeycloak(query, signal) {
    return simular(signal, () => {
      const termo = query.trim().toLowerCase();
      if (termo.length < 2) throw erro('a busca de usuários', 422, 'Digite ao menos 2 caracteres para buscar.');
      return USUARIOS.filter((item) => item.email.endsWith('@nasajon.com.br')).filter((item) =>
        [item.nome, item.username, item.email].some((campo) => campo.toLowerCase().includes(termo)),
      );
    });
  },

  obterCapabilitiesDoUsuario(id, signal) {
    return simular(signal, (): CapabilitiesDoUsuario => {
      const usuario = acharUsuario('as capabilities do usuário', id);
      return {
        keycloak_user_id: id,
        email: usuario.email,
        capabilities: obterEstado().capabilities.get(id) ?? [],
      };
    });
  },

  async concederCapability(id, capability) {
    const roles = await getAuthPort().getRoles();
    return simular(undefined, () => {
      const recurso = 'a concessão da capability';
      validarEscrita(recurso, capability, roles);
      const usuario = acharUsuario(recurso, id);
      const atuais = obterEstado().capabilities.get(id) ?? [];
      if (atuais.includes(capability)) return; // idempotente
      obterEstado().capabilities.set(id, [...atuais, capability]);
      registrar(usuario, capability, 'conceder');
    });
  },

  async revogarCapability(id, capability) {
    const roles = await getAuthPort().getRoles();
    return simular(undefined, () => {
      const recurso = 'a revogação da capability';
      validarEscrita(recurso, capability, roles);
      const usuario = acharUsuario(recurso, id);
      const atuais = obterEstado().capabilities.get(id) ?? [];
      if (!atuais.includes(capability)) return;
      obterEstado().capabilities.set(
        id,
        atuais.filter((item) => item !== capability),
      );
      registrar(usuario, capability, 'revogar');
    });
  },

  listarEventosCapabilities(limit = 50, signal) {
    return simular(signal, () => obterEstado().eventos.slice(0, limit));
  },
};
